export default function Usage() {
  const steps = [
    {
      number: '01',
      title: 'Install Bellibutton',
      description: 'Install the CLI tool globally or add it to your project dependencies',
      code: 'npm install -g bellibutton',
      color: '#00d9ff'
    },
    {
      number: '02',
      title: 'Create Configuration',
      description: 'Define your lint rules in a simple, language-agnostic format',
      code: 'bellibutton init',
      color: '#8b5cf6'
    },
    {
      number: '03',
      title: 'Export to Languages',
      description: 'Generate language-specific configurations from your universal rules',
      code: 'bellibutton export --to eslint,pylint',
      color: '#00d9ff'
    },
    {
      number: '04',
      title: 'Sync Across Projects',
      description: 'Keep all your projects in sync with a single source of truth',
      code: 'bellibutton sync',
      color: '#8b5cf6'
    }
  ];

  const examples = [
    {
      title: 'JavaScript Project',
      description: 'Export to ESLint configuration',
      command: 'bellibutton export --to eslint --output .eslintrc.json'
    },
    {
      title: 'Python Project',
      description: 'Export to Pylint configuration',
      command: 'bellibutton export --to pylint --output .pylintrc'
    },
    {
      title: 'Rust Project',
      description: 'Export to Rustfmt configuration',
      command: 'bellibutton export --to rustfmt --output rustfmt.toml'
    },
    {
      title: 'Multi-Language Monorepo',
      description: 'Export to all supported formats',
      command: 'bellibutton export --to all'
    }
  ];

  return (
    <section id="usage" className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#00d9ff]/5 to-transparent"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#00d9ff]/10 border border-[#00d9ff]/30 px-4 py-2 rounded-full mb-6">
            <i className="ri-play-circle-line text-[#00d9ff]"></i>
            <span className="text-[#00d9ff] text-sm font-medium">Usage</span>
          </div>
          <h2 className="text-5xl font-bold text-white mb-6 font-['Space_Grotesk']">
            How It Works
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Get up and running with Bellibutton in four simple steps
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-2xl p-8 hover:border-white/20 transition-all duration-300"
            >
              <div className="flex items-start gap-6">
                <div
                  className="text-6xl font-bold opacity-10"
                  style={{ color: step.color }}
                >
                  {step.number}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-gray-400 mb-6">{step.description}</p>
                  <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-terminal-line text-gray-500 text-sm"></i>
                      <span className="text-gray-500 text-xs font-mono">Terminal</span>
                    </div>
                    <code className="text-sm font-mono" style={{ color: step.color }}>
                      {step.code}
                    </code>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-3xl p-12">
          <h3 className="text-3xl font-bold text-white mb-8 text-center font-['Space_Grotesk']">
            Common Use Cases
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {examples.map((example, index) => (
              <div
                key={index}
                className="bg-[#0a0a0a] border border-white/10 rounded-xl p-6 hover:border-[#00d9ff]/50 transition-all duration-300"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#00d9ff]/20 to-[#8b5cf6]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-code-s-slash-line text-[#00d9ff]"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{example.title}</h4>
                    <p className="text-gray-500 text-sm">{example.description}</p>
                  </div>
                </div>
                <div className="bg-black/30 rounded-lg p-3 font-mono text-sm text-[#00d9ff] overflow-x-auto">
                  {example.command}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href="#"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#00d9ff] to-[#00b8d4] text-black font-semibold rounded-xl hover:shadow-lg hover:shadow-[#00d9ff]/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line"></i>
              <span>View Full Documentation</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
